import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  SkillDepartmentStarMapCellResponse,
  SkillDepartmentStarMapEmployeeResponse,
  SkillDepartmentStarMapResponse,
  SkillDepartmentStarMapSpecializationResponse,
} from '../../../models/skill-learning.model';
import { SkillProgressApiService } from '../../../services/skill-progress-api.service';
import {
  createInitialDepartmentStarMap,
  createInitialDepartmentStarMapTotals,
  DEPARTMENT_STAR_MAP_SORT,
  DepartmentStarMapSort,
} from '../../../options/department-star-map.option';

@Component({
  selector: 'app-department-star-map',
  templateUrl: './department-star-map.component.html',
  styleUrls: ['./department-star-map.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentStarMapComponent implements OnInit, OnDestroy {

  public starMap: SkillDepartmentStarMapResponse | null = null;
  public visibleEmployees: SkillDepartmentStarMapEmployeeResponse[] = [];
  public isLoading = true;
  public hasError = false;
  public selectedSort: DepartmentStarMapSort = DEPARTMENT_STAR_MAP_SORT.SURNAME;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly progressApi: SkillProgressApiService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    const groupId = this.route.snapshot.params['groupId'];
    if (!groupId) {
      this.handleStarMapLoadFailure();
      return;
    }
    this.loadStarMapAction(groupId);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSortBySurname(): void {
    this.selectSort(DEPARTMENT_STAR_MAP_SORT.SURNAME);
  }

  public onSortByCompletedLevels(): void {
    this.selectSort(DEPARTMENT_STAR_MAP_SORT.COMPLETED_LEVELS);
  }

  public trackByEmployeeId(index: number, employee: SkillDepartmentStarMapEmployeeResponse): string {
    return employee && employee.id ? employee.id : String(index);
  }

  public trackBySpecializationId(index: number, specialization: SkillDepartmentStarMapSpecializationResponse): string {
    return specialization && specialization.id ? specialization.id : String(index);
  }

  public trackByCellSpecializationId(index: number, cell: SkillDepartmentStarMapCellResponse): string {
    return cell && cell.specializationId ? cell.specializationId : String(index);
  }

  public getCellClass(
    cell: SkillDepartmentStarMapCellResponse
  ): string {
    const level = this.normalizeLevel(cell ? cell.level : 0);
    return `department-star-map__cell--level-${level}`;
  }

  public buildCellTitle(
    cell: SkillDepartmentStarMapCellResponse,
    specialization: SkillDepartmentStarMapSpecializationResponse
  ): string {
    const name = specialization ? specialization.name : '';
    const level = cell ? this.normalizeLevel(cell.level) : 0;
    return `${name}: level ${level}`;
  }

  private loadStarMapAction(groupId: string): void {
    this.isLoading = true;
    this.hasError = false;
    this.progressApi.fetchDepartmentStarMap(groupId)
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.handleStarMapLoadSuccess(response),
        () => this.handleStarMapLoadFailure(),
      );
  }

  private handleStarMapLoadSuccess(response: SkillDepartmentStarMapResponse): void {
    this.starMap = this.normalizeStarMap(response);
    this.isLoading = false;
    this.hasError = false;
    this.applySorting();
  }

  private handleStarMapLoadFailure(): void {
    this.starMap = null;
    this.visibleEmployees = [];
    this.isLoading = false;
    this.hasError = true;
    this.cdr.markForCheck();
  }

  private normalizeStarMap(response: SkillDepartmentStarMapResponse): SkillDepartmentStarMapResponse {
    const starMap = response || createInitialDepartmentStarMap();
    return Object.assign({}, starMap, {
      specializations: starMap.specializations || [],
      employees: this.normalizeEmployees(starMap.employees || []),
      totals: starMap.totals || createInitialDepartmentStarMapTotals(),
    });
  }

  private normalizeEmployees(
    employees: SkillDepartmentStarMapEmployeeResponse[]
  ): SkillDepartmentStarMapEmployeeResponse[] {
    return employees.map(employee => Object.assign({}, employee, {
      cells: employee.cells || [],
      fullName: employee.fullName || this.buildFullName(employee),
      positionName: employee.positionName || '',
    }));
  }

  private buildFullName(employee: SkillDepartmentStarMapEmployeeResponse): string {
    return [employee.lastName, employee.firstName].filter(Boolean).join(' ');
  }

  private applySorting(): void {
    if (!this.starMap) {
      this.visibleEmployees = [];
      this.cdr.markForCheck();
      return;
    }
    this.visibleEmployees = this.starMap.employees.slice().sort((left, right) => this.compareEmployees(left, right));
    this.cdr.markForCheck();
  }

  private compareEmployees(
    left: SkillDepartmentStarMapEmployeeResponse,
    right: SkillDepartmentStarMapEmployeeResponse
  ): number {
    if (this.selectedSort === DEPARTMENT_STAR_MAP_SORT.COMPLETED_LEVELS) {
      return this.compareByCompletedLevels(left, right);
    }
    return this.compareBySurname(left, right);
  }

  private compareByCompletedLevels(
    left: SkillDepartmentStarMapEmployeeResponse,
    right: SkillDepartmentStarMapEmployeeResponse
  ): number {
    const levelsDifference = this.calculateCompletedLevels(right) - this.calculateCompletedLevels(left);
    return levelsDifference || this.compareBySurname(left, right);
  }

  private calculateCompletedLevels(employee: SkillDepartmentStarMapEmployeeResponse): number {
    if (!employee || !employee.cells) {
      return 0;
    }
    return employee.cells.reduce((total, cell) => total + this.normalizeLevel(cell ? cell.level : 0), 0);
  }

  private compareBySurname(
    left: SkillDepartmentStarMapEmployeeResponse,
    right: SkillDepartmentStarMapEmployeeResponse
  ): number {
    return this.compareText(left.lastName, right.lastName)
      || this.compareText(left.firstName, right.firstName)
      || this.compareText(left.fullName, right.fullName);
  }

  private compareText(left: string, right: string): number {
    return this.normalizeSortText(left).localeCompare(this.normalizeSortText(right));
  }

  private normalizeSortText(value: string): string {
    return value ? value.trim().toLowerCase() : '';
  }

  private selectSort(sort: DepartmentStarMapSort): void {
    if (this.selectedSort === sort) {
      return;
    }
    this.selectedSort = sort;
    this.applySorting();
  }

  private normalizeLevel(level: number): number {
    if (level < 0) {
      return 0;
    }
    if (level > 3) {
      return 3;
    }
    return level || 0;
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/department-star-map/department-star-map.component.ts